import { Locales } from '@griply/types';

import {
  getDayMap,
  getMonthMap,
  getRruleKeywords,
  WORD_END,
  WORD_START,
} from './locale-vocabulary';

// Keywords used for date parsing in both English and Dutch. Includes abbreviations.
// The order here doesn't matter as they are compiled into a single regex pattern.
// Both Portuguese variants share one date grammar; only UI wording differs.
const portugueseDateKeywords: string[] = [
  'hoje',
  'amanhã',
  'amanha',
  'depois de amanhã',
  'depois de amanha',
  'na próxima semana',
  'próxima semana',
  'proxima semana',
  'no próximo mês',
  'próximo mês',
  'proximo mes',
  'no próximo ano',
  'próximo ano',
  'proximo ano',
  'este fim de semana',
  'no próximo fim de semana',
  'próximo fim de semana',
  'fim do mês',
  'fim do mes',
  'próxima segunda',
  'proxima segunda',
  'segunda que vem',
  'próxima terça',
  'próxima terca',
  'proxima terça',
  'proxima terca',
  'terça que vem',
  'terca que vem',
  'próxima quarta',
  'proxima quarta',
  'quarta que vem',
  'próxima quinta',
  'proxima quinta',
  'quinta que vem',
  'próxima sexta',
  'proxima sexta',
  'sexta que vem',
  'próxima sábado',
  'próxima sabado',
  'proxima sábado',
  'proxima sabado',
  'sábado que vem',
  'sabado que vem',
  'próxima domingo',
  'proxima domingo',
  'domingo que vem',
];

const DATE_KEYWORDS: Record<Locales, string[]> = {
  [Locales.EnUS]: [
    'today',
    'day after tomorrow',
    'tod',
    'tomorrow',
    'tmw',
    'tmr',
    'next monday',
    'next mon',
    'next tuesday',
    'next tue',
    'next wednesday',
    'next wed',
    'next thursday',
    'next thu',
    'next friday',
    'next fri',
    'next saturday',
    'next sat',
    'next sunday',
    'next sun',
    'next week',
    'next month',
    'end of month',
    'this weekend',
    'next weekend',
    'next year',
  ],
  [Locales.NlNL]: [
    'vandaag',
    'overmorgen',
    'morgen',
    'volgende week',
    'volgende maand',
    'volgend jaar',
    'dit weekend',
    'volgend weekend',
    'eind van de maand',
    'volgende maandag',
    'volgende dinsdag',
    'volgende woensdag',
    'volgende donderdag',
    'volgende vrijdag',
    'volgende zaterdag',
    'volgende zondag',
  ],
  [Locales.EsES]: [
    'hoy',
    'pasado mañana',
    'pasado manana',
    'mañana',
    'la proxima semana',
    'la próxima semana',
    'proxima semana',
    'próxima semana',
    'el proximo mes',
    'el próximo mes',
    'proximo mes',
    'próximo mes',
    'el proximo año',
    'el próximo año',
    'proximo año',
    'próximo año',
    'este fin de semana',
    'el proximo fin de semana',
    'el próximo fin de semana',
    'proximo fin de semana',
    'próximo fin de semana',
    'fin de mes',
    'próximo lunes',
    'próximo martes',
    'próximo miércoles',
    'próximo jueves',
    'próximo viernes',
    'próximo sábado',
    'próximo domingo',
    'proximo lunes',
    'proximo martes',
    'proximo miercoles',
    'proximo jueves',
    'proximo viernes',
    'proximo sabado',
    'proximo domingo',
    'el próximo lunes',
    'el próximo martes',
    'el próximo miércoles',
    'el próximo jueves',
    'el próximo viernes',
    'el próximo sábado',
    'el próximo domingo',
    'el lunes que viene',
    'el martes que viene',
    'el miércoles que viene',
    'el jueves que viene',
    'el viernes que viene',
    'el sábado que viene',
    'el domingo que viene',
    'lunes que viene',
    'martes que viene',
    'miércoles que viene',
    'jueves que viene',
    'viernes que viene',
    'sábado que viene',
    'domingo que viene',
    'mie',
    'sab',
  ],
  [Locales.DeDE]: [
    'heute',
    'morgen',
    'übermorgen',
    'uebermorgen',
    'nächste woche',
    'naechste woche',
    'nächsten monat',
    'naechsten monat',
    'nächstes jahr',
    'naechstes jahr',
    'dieses wochenende',
    'nächstes wochenende',
    'naechstes wochenende',
    'ende des monats',
    'nächsten montag',
    'naechsten montag',
    'nächsten dienstag',
    'naechsten dienstag',
    'nächsten mittwoch',
    'naechsten mittwoch',
    'nächsten donnerstag',
    'naechsten donnerstag',
    'nächsten freitag',
    'naechsten freitag',
    'nächsten samstag',
    'naechsten samstag',
    'nächsten sonntag',
    'naechsten sonntag',
    'nächsten mo',
    'naechsten mo',
    'nächsten di',
    'naechsten di',
    'nächsten mi',
    'naechsten mi',
    'nächsten do',
    'naechsten do',
    'nächsten fr',
    'naechsten fr',
    'nächsten sa',
    'naechsten sa',
    'nächsten so',
    'naechsten so',
  ],
  // Every diacritic form is paired with an ASCII twin — "ł" survives NFD, so folding cannot help.
  [Locales.PlPL]: [
    'dziś',
    'dzis',
    'dzisiaj',
    'jutro',
    'pojutrze',
    'w przyszłym tygodniu',
    'w przyszlym tygodniu',
    'przyszły tydzień',
    'przyszly tydzien',
    'w przyszłym miesiącu',
    'w przyszlym miesiacu',
    'przyszły miesiąc',
    'przyszly miesiac',
    'w przyszłym roku',
    'w przyszlym roku',
    'przyszły rok',
    'przyszly rok',
    'w ten weekend',
    'ten weekend',
    'w przyszły weekend',
    'w przyszly weekend',
    'przyszły weekend',
    'przyszly weekend',
    'koniec miesiąca',
    'koniec miesiaca',
    'w przyszły poniedziałek',
    'w przyszly poniedzialek',
    'w przyszły wtorek',
    'w przyszly wtorek',
    'w przyszłą środę',
    'w przyszla srode',
    'w przyszły czwartek',
    'w przyszly czwartek',
    'w przyszły piątek',
    'w przyszly piatek',
    'w przyszłą sobotę',
    'w przyszla sobote',
    'w przyszłą niedzielę',
    'w przyszla niedziele',
  ],
  [Locales.DaDK]: [
    'i dag',
    'idag',
    'i morgen',
    'imorgen',
    'i overmorgen',
    'næste uge',
    'naeste uge',
    'næste måned',
    'naeste maaned',
    'næste år',
    'naeste aar',
    'denne weekend',
    'næste weekend',
    'naeste weekend',
    'slutningen af måneden',
    'slutningen af maaneden',
    'næste mandag',
    'naeste mandag',
    'naeste mandag',
    'næste tirsdag',
    'naeste tirsdag',
    'naeste tirsdag',
    'næste onsdag',
    'naeste onsdag',
    'naeste onsdag',
    'næste torsdag',
    'naeste torsdag',
    'naeste torsdag',
    'næste fredag',
    'naeste fredag',
    'naeste fredag',
    'næste lørdag',
    'naeste lordag',
    'naeste lørdag',
    'naeste lordag',
    'næste søndag',
    'naeste sondag',
    'naeste søndag',
    'naeste sondag',
  ],
  [Locales.SvSE]: [
    'i dag',
    'idag',
    'i morgon',
    'imorgon',
    'i övermorgon',
    'nästa vecka',
    'nasta vecka',
    'nästa månad',
    'nasta manad',
    'nästa år',
    'nasta ar',
    'denna helg',
    'nästa helg',
    'nasta helg',
    'slutet av månaden',
    'slutet av manaden',
    'nästa måndag',
    'nasta maandag',
    'nasta måndag',
    'nasta maandag',
    'nästa tisdag',
    'nasta tisdag',
    'nasta tisdag',
    'nästa onsdag',
    'nasta onsdag',
    'nasta onsdag',
    'nästa torsdag',
    'nasta torsdag',
    'nasta torsdag',
    'nästa fredag',
    'nasta fredag',
    'nasta fredag',
    'nästa lördag',
    'nasta lordag',
    'nasta lördag',
    'nasta lordag',
    'nästa söndag',
    'nasta sondag',
    'nasta söndag',
    'nasta sondag',
  ],
  [Locales.NbNO]: [
    'i dag',
    'idag',
    'i morgen',
    'imorgen',
    'i overmorgen',
    'neste uke',
    'neste måned',
    'neste maaned',
    'neste år',
    'neste aar',
    'denne helg',
    'denne helgen',
    'neste helg',
    'slutten av måneden',
    'slutten av maaneden',
    'neste mandag',
    'neste tirsdag',
    'neste onsdag',
    'neste torsdag',
    'neste fredag',
    'neste lørdag',
    'neste lordag',
    'neste søndag',
    'neste sondag',
  ],
  [Locales.ItIT]: [
    'oggi',
    'domani',
    'dopodomani',
    'la prossima settimana',
    'prossima settimana',
    'il prossimo mese',
    'prossimo mese',
    'il prossimo anno',
    'prossimo anno',
    'questo weekend',
    'questo fine settimana',
    'il prossimo weekend',
    'prossimo weekend',
    'fine mese',
    'fine del mese',
    'prossimo lunedì',
    'prossimo lunedi',
    'lunedì prossimo',
    'lunedi prossimo',
    'prossima lunedì',
    'prossima lunedi',
    'lunedì prossima',
    'lunedi prossima',
    'prossimo martedì',
    'prossimo martedi',
    'martedì prossimo',
    'martedi prossimo',
    'prossima martedì',
    'prossima martedi',
    'martedì prossima',
    'martedi prossima',
    'prossimo mercoledì',
    'prossimo mercoledi',
    'mercoledì prossimo',
    'mercoledi prossimo',
    'prossima mercoledì',
    'prossima mercoledi',
    'mercoledì prossima',
    'mercoledi prossima',
    'prossimo giovedì',
    'prossimo giovedi',
    'giovedì prossimo',
    'giovedi prossimo',
    'prossima giovedì',
    'prossima giovedi',
    'giovedì prossima',
    'giovedi prossima',
    'prossimo venerdì',
    'prossimo venerdi',
    'venerdì prossimo',
    'venerdi prossimo',
    'prossima venerdì',
    'prossima venerdi',
    'venerdì prossima',
    'venerdi prossima',
    'prossimo sabato',
    'sabato prossimo',
    'prossima sabato',
    'sabato prossima',
    'prossimo domenica',
    'domenica prossimo',
    'prossima domenica',
    'domenica prossima',
  ],
  [Locales.FrFR]: [
    "aujourd'hui",
    'aujourd hui',
    'demain',
    'après-demain',
    'apres-demain',
    'la semaine prochaine',
    'semaine prochaine',
    'le mois prochain',
    'mois prochain',
    "l'année prochaine",
    'année prochaine',
    'annee prochaine',
    'ce week-end',
    'ce weekend',
    'le week-end prochain',
    'week-end prochain',
    'fin du mois',
    'fin de mois',
    'lundi prochain',
    'prochain lundi',
    'mardi prochain',
    'prochain mardi',
    'mercredi prochain',
    'prochain mercredi',
    'jeudi prochain',
    'prochain jeudi',
    'vendredi prochain',
    'prochain vendredi',
    'samedi prochain',
    'prochain samedi',
    'dimanche prochain',
    'prochain dimanche',
  ],
  [Locales.PtPT]: portugueseDateKeywords,
  [Locales.PtBR]: portugueseDateKeywords,
};

// Phrases that explicitly clear the date instead of resolving to one.
export const CLEAR_DATE_KEYWORDS: Record<Locales, string[]> = {
  [Locales.EnUS]: ['no date'],
  [Locales.NlNL]: ['geen datum'],
  [Locales.EsES]: ['sin fecha'],
  [Locales.DeDE]: ['kein datum'],
  [Locales.PlPL]: ['bez daty'],
  [Locales.DaDK]: ['ingen dato'],
  [Locales.SvSE]: ['inget datum'],
  [Locales.NbNO]: ['ingen dato'],
  [Locales.ItIT]: ['nessuna data'],
  [Locales.FrFR]: ['aucune date'],
  [Locales.PtPT]: ['sem data'],
  [Locales.PtBR]: ['sem data'],
};

// Locales that accept a bare article before a day number ("el 27").
export const DAY_NUMBER_ARTICLES: Partial<Record<Locales, string>> = {
  [Locales.EsES]: 'el',
};

// Generates a comprehensive regex pattern for all supported date formats.
export const getAllDatePatterns = (language: Locales) => {
  // Bare weekday names come from dayMap so the two sources cannot drift apart.
  const dateKeywords = [
    ...DATE_KEYWORDS[language],
    ...CLEAR_DATE_KEYWORDS[language],
    ...Object.keys(getDayMap(language)),
  ]
    .sort((a, b) => b.length - a.length)
    .join('|');
  const monthNames = Object.keys(getMonthMap(language))
    .sort((a, b) => b.length - a.length)
    .join('|');
  const ordinals = getRruleKeywords(language).ordinals;
  const dayNumberArticle = DAY_NUMBER_ARTICLES[language];

  const patterns = [
    `(?:${monthNames}) \\d{1,2} \\d{4}`, // mmm dd yyyy
    `\\d{1,2} (?:${monthNames}) \\d{4}`, // dd mmm yyyy
    `\\d{1,2} (?:${monthNames})`, // dd mmm
    `(?:${monthNames}) \\d{1,2}`, // mmm dd
    `\\d{1,2}${ordinals}`, // 27th, 27., 27e, etc.
    `\\d{1,2}[\\/\\.]\\d{1,2}(?:[\\/\\.]\\d{2,4})?`, // dd/mm/yyyy or dd.mm.yyyy
    `\\d{4}-\\d{1,2}-\\d{1,2}`, // yyyy-mm-dd
    `${WORD_START}(?:${dateKeywords})${WORD_END}`, // today, tomorrow, etc.
    ...(dayNumberArticle ? [`${dayNumberArticle}\\s\\d{1,2}`] : []),
  ];

  return `(?:${patterns.join('|')})`;
};
